import {
  CustomButton,
  CustomCollapseCard,
  CustomForm,
  CustomFormItem,
  CustomImage,
  CustomInput,
  CustomMaskInput,
  CustomNumberInput,
  CustomSelect,
  Option,
  Text,
  useText,
} from '@src/components';
import '@src/styles/manageMyCards/cardFilter.scss';
import { VouIdCode, filterCardType } from '@src/store/enums';
import iconSearchWhite from '@src/assets/icons/icon-white-search.svg';
import { Form, Collapse } from 'antd';
import React, { useCallback, useMemo, useState } from 'react';
import {
  cardGetPagedList,
  setAddressIdList,
  clearAddressIdList,
} from '@src/store/slice/manageMyCardsSlice';
import { useDispatch, useSelector } from 'react-redux';
import CustomMultiSelect from '@src/components/CustomMultiSelect';
import PropTypes from 'prop-types';

const { Panel } = Collapse;

const CardFilter = ({ isCompliments = false, isMarket = false, resetPagination = null, activeCardListType = 2 }) => {
  const [selectedAddressListReset, setSelectedAddressListReset] = useState(false);
  const [form] = Form.useForm();
  const dispatch = useDispatch();

  const { filterObject } = useSelector((state) => state?.manageMyCards);
  const { addressListByUserQuery } = useSelector((state) => state?.address);

  // 1. Move all useText hook calls to the very top, before any conditional logic or returns
  const cardStateText = useText('cardStatus2');
  const phoneNumberText = useText('phoneNumber');
  const namePlaceholder = useText('name');
  const surnamePlaceholder = useText('surname');
  const deliveryAddressText = useText('deliveryAddress2');
  const cardNumberPlaceholder = useText('cardNumber2');
  const refNoPlaceholder = useText('referenceCode2');

  let vouId;

  if (isMarket) {
    vouId = VouIdCode.Market;
  } else if (isCompliments) {
    vouId = VouIdCode.Compliments;
  } else {
    vouId = VouIdCode.Restaurant;
  }

  const handleSearch = useCallback(async () => {
    try {
      const values = await form.validateFields();
      let newGsm = values?.gsm
        ?.replace(/\)/g, '')
        ?.replace(/\(/g, '')
        ?.replace(/-/g, '')
        ?.replace(/ /g, '');

      let body = {
        ...values,
        gsm: newGsm?.replace('+90', ''),
        vouId: vouId,
        cardNumber: values?.cardNumber && String(values?.cardNumber),
      };

      if (!isCompliments && !isMarket) {
        body.statusId = values?.statusId;
        body.vouId = VouIdCode.Restaurant;
      } else {
        body.statusId = activeCardListType === 3 ? 50 : activeCardListType === 4 ? 60 : null;
      }

      await dispatch(setAddressIdList(values?.addressIdList));

      if ((isCompliments || isMarket) && resetPagination) {
        resetPagination();
      }

      await dispatch(
        cardGetPagedList({
          ...filterObject,
          pageNumber: 1,
          pageSize: (isCompliments || isMarket) ? 100 : undefined,
          body,
        }),
      );
    } catch (e) { }
  }, [dispatch, filterObject, form, vouId, isCompliments, isMarket, resetPagination, activeCardListType]);

  const handleClear = useCallback(async () => {
    await dispatch(clearAddressIdList([]));

    if ((isCompliments || isMarket) && resetPagination) {
      resetPagination();
    }

    let statusId = null;

    if ((isCompliments || isMarket)) {
      statusId = activeCardListType === 3 ? 50 : activeCardListType === 4 ? 60 : null;
    }

    await dispatch(
      cardGetPagedList({
        body: { vouId: vouId, statusId },
        pageNumber: 1,
        pageSize: (isCompliments || isMarket) ? 100 : undefined,
      }),
    );

    form.resetFields();
    setSelectedAddressListReset(true);
  }, [dispatch, form, activeCardListType, isCompliments, isMarket, resetPagination, vouId]);

  const addressListForMultiSelect = useMemo(
    () =>
      addressListByUserQuery?.map((item) => {
        return {
          label: item.addressName,
          value: item.id,
        };
      }),
    [addressListByUserQuery],
  );

  const addressValueChange = async (values) => {
    form.setFieldsValue({ addressIdList: values.map((item) => ({ addressId: item.value })) });
    setSelectedAddressListReset(false);
  };

  // Early return is here. Since all hooks are at the top, this no longer causes an issue.
  if (isCompliments || isMarket) {
    return (
      <div className="compliment-card-list-filter-container">
        <Collapse className="card-filter-card" defaultActiveKey={['1']} ghost>
          <Panel collapsible="disabled" showArrow={false} key="1">
            <CustomForm
              name="filterForm"
              className="filter-form"
              autoComplete="off"
              initialValues={{
                name: filterObject?.body?.name || '',
                surname: filterObject?.body?.surname || '',
                cardNumber: filterObject?.body?.cardNumber || '',
                refNo: filterObject?.body?.refNo || '',
                addressIdList: filterObject?.body?.addressId ? [filterObject?.body?.addressId] : [],
                gsm: filterObject?.body?.gsm || '',
              }}
              layout={'horizontal'}
              form={form}
            >
              <div className="form-item">
                <div className="card-filter-row">
                  <CustomFormItem
                    name="name"
                    label={
                      <div style={{ fontFamily: 'UbuntuMedium, sans-serif' }}>
                        <Text t="name2" />
                      </div>
                    }
                  >
                    <CustomInput
                      id="ticketRestaurant-manageMyCards-CardFilter-name-input"
                      autoComplete="off"
                      height={36}
                    />
                  </CustomFormItem>
                  <CustomFormItem
                    name="surname"
                    label={
                      <div style={{ fontFamily: 'UbuntuMedium, sans-serif' }}>
                        <Text t="surname3" />
                      </div>
                    }
                  >
                    <CustomInput
                      id="ticketRestaurant-manageMyCards-CardFilter-surname-input"
                      autoComplete="off"
                      height={36}
                    />
                  </CustomFormItem>
                  <CustomFormItem
                    name="cardNumber"
                    label={
                      <div style={{ fontFamily: 'UbuntuMedium, sans-serif' }}>
                        <Text t="cardNumber3" />
                      </div>
                    }
                  >
                    <CustomNumberInput
                      id="ticketRestaurant-manageMyCards-CardFilter-cardNumber-numberInput"
                      autoComplete="off"
                      height={36}
                    />
                  </CustomFormItem>
                </div>
                <div className="card-filter-row">
                  <CustomFormItem
                    name="refNo"
                    label={
                      <div style={{ fontFamily: 'UbuntuMedium, sans-serif' }}>
                        <Text t="referenceCode" />
                      </div>
                    }
                  >
                    <CustomInput
                      id="ticketRestaurant-manageMyCards-CardFilter-refNo-input"
                      autoComplete="off"
                      height={36}
                    />
                  </CustomFormItem>
                  <div className="card-filter-address">
                    <CustomFormItem
                      name="addressIdList"
                      label={
                        <div style={{ fontFamily: 'UbuntuMedium, sans-serif' }}>
                          <Text t="deliveryAddress2" />
                        </div>
                      }
                    ></CustomFormItem>
                    <CustomMultiSelect
                      source={addressListForMultiSelect}
                      reset={selectedAddressListReset}
                      onCustomMultiSelectChange={addressValueChange}
                      selectSomeItems={' '}
                      marginBottom={false}
                    />
                  </div>
                  <CustomFormItem
                    name="gsm"
                    rules={[]}
                    label={
                      <div style={{ fontFamily: 'UbuntuMedium, sans-serif' }}>
                        <Text t="mobilePhone2" />
                      </div>
                    }
                  >
                    <CustomMaskInput
                      id="ticketRestaurant-manageMyCards-CardFilter-gsm-maskInput"
                      mask={'+\\90 (999) 999 99 99'}
                    >
                      <CustomInput
                        id="ticketRestaurant-manageMyCards-CardFilter-gsm-input"
                        autoComplete="off"
                        width={235}
                        height={36}
                      />
                    </CustomMaskInput>
                  </CustomFormItem>
                </div>
                <div className="form-footer">
                  <div className="action-buttons">
                    <CustomButton
                      id="ticketRestaurant-manageMyCards-CardFilter-clear-btn"
                      className="clear-btn"
                      onClick={handleClear}
                    >
                      <Text t="clean" /></CustomButton>
                    <CustomButton
                      id="ticketRestaurant-manageMyCards-CardFilter-search-btn"
                      className="search-btn"
                      onClick={handleSearch}
                      style={{ backgroundColor: '#334155' }}
                    >
                      <CustomImage className="icon-search" src={iconSearchWhite} /> <Text t="search" /></CustomButton>
                  </div>
                </div>
              </div>
            </CustomForm>
          </Panel>
        </Collapse>
      </div>
    );
  }

  return (
    <div className="card-list-filter-container">
      <Collapse className="card-filter-card" defaultActiveKey={['1']} ghost>
        <div className="card-filter-header"><Text t="addFilter" /></div>
        <Panel collapsible="disabled" showArrow={false} key="1">
          <CustomForm
            name="filterForm"
            className="filter-form"
            autoComplete="off"
            initialValues={{
              name: filterObject?.body?.name || '',
              surname: filterObject?.body?.surname || '',
              cardNumber: filterObject?.body?.cardNumber || '',
              refNo: filterObject?.body?.refNo || '',
              addressIdList: filterObject?.body?.addressId ? [filterObject?.body?.addressId] : [],
              statusId: filterObject?.body?.statusId || null,
              gsm: filterObject?.body?.gsm || '',
            }}
            layout={'horizontal'}
            form={form}
          >
            <div className="form-item">
              <CustomFormItem name="name">
                <CustomInput
                  id="ticketRestaurant-manageMyCards-CardFilter-name-input"
                  autoComplete="off"
                  height={36}
                  placeholder={namePlaceholder} // 2. Applied variable
                />
              </CustomFormItem>
              <CustomFormItem name="surname">
                <CustomInput
                  id="ticketRestaurant-manageMyCards-CardFilter-surname-input"
                  autoComplete="off"
                  height={36}
                  placeholder={surnamePlaceholder} // 2. Applied variable
                />
              </CustomFormItem>
              <CustomFormItem name="cardNumber">
                <CustomNumberInput
                  id="ticketRestaurant-manageMyCards-CardFilter-cardNumber-numberInput"
                  autoComplete="off"
                  placeholder={cardNumberPlaceholder}
                  height={36}
                />
              </CustomFormItem>
              <CustomFormItem name="refNo">
                <CustomInput
                  id="ticketRestaurant-manageMyCards-CardFilter-refNo-input"
                  autoComplete="off"
                  placeholder={refNoPlaceholder}
                  height={36}
                />
              </CustomFormItem>
              <CustomFormItem name="statusId">
                <CustomSelect
                  id="ticketRestaurant-manageMyCards-CardFilter-statusId-select"
                  placeholder={cardStateText}
                  height={36}
                >
                  {filterCardType.map((card) => (
                    <Option
                      id={`ticketRestaurant-manageMyCards-CardFilter-statusId-${card?.text}-option`}
                      key={card?.value}
                      value={card?.value}
                    >
                      {card?.text}
                    </Option>
                  ))}
                </CustomSelect>
              </CustomFormItem>
              <CustomFormItem name="gsm" rules={[]}>
                <CustomMaskInput
                  id="ticketRestaurant-manageMyCards-CardFilter-gsm-maskInput"
                  mask={'+\\90 (999) 999 99 99'}
                >
                  <CustomInput
                    id="ticketRestaurant-manageMyCards-CardFilter-gsm-input"
                    placeholder={phoneNumberText}
                    autoComplete="off"
                    width={235}
                    height={36}
                  />
                </CustomMaskInput>
              </CustomFormItem>
              <CustomFormItem name="addressIdList" style={{ display: 'none' }}></CustomFormItem>
              <CustomMultiSelect
                source={addressListForMultiSelect}
                reset={selectedAddressListReset}
                onCustomMultiSelectChange={addressValueChange}
                selectSomeItems={deliveryAddressText} // 2. Applied variable
              />

              <div className="form-footer">
                <div className="action-buttons">
                  <CustomButton
                    id="ticketRestaurant-manageMyCards-CardFilter-clear-btn"
                    className="clear-btn"
                    onClick={handleClear}
                  >
                    <Text t="clean" /></CustomButton>
                  <CustomButton
                    id="ticketRestaurant-manageMyCards-CardFilter-search-btn"
                    className="search-btn"
                    onClick={handleSearch}
                  >
                    <CustomImage className="icon-search" src={iconSearchWhite} /> <Text t="search" /></CustomButton>
                </div>
              </div>
            </div>
          </CustomForm>
        </Panel>
      </Collapse>
    </div>
  );
};

CardFilter.propTypes = {
  isCompliments: PropTypes.bool,
  resetPagination: PropTypes.func,
  activeCardListType: PropTypes.number,
  isMarket: PropTypes.bool,
};
export default CardFilter;